import type {CaptureDefaultValues} from 'modules/VendorRegister/types';

import {Button, Loading, Modal} from '@ds/components';
import {toast} from '@Toast';
import {useEffect, useRef, useState} from 'react';
import {useAppDispatch} from 'store';
import {uploadFile} from 'store/slices/vendor-registration/thunk';

import {createImagePreview} from './utils';

type CapturePhase = 'camera' | 'preview';

type ImageCaptureModalProps = {
  show: boolean;
  onClose: () => void;
  onCaptureComplete: (id: string | number, file: File) => void;
  defaultValues: CaptureDefaultValues;
};

const ImageCaptureModal: React.FC<ImageCaptureModalProps> = ({
  show,
  onClose,
  onCaptureComplete,
  defaultValues
}) => {
  const dispatch = useAppDispatch();
  const liveVideoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const capturedUrlRef = useRef<string | null>(null);
  const captureRequestRef = useRef(0);
  const [cameraLoading, setCameraLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [phase, setPhase] = useState<CapturePhase>('camera');
  const [capturedUrl, setCapturedUrl] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const setPreviewUrl = (url: string | null) => {
    if (capturedUrlRef.current) {
      URL.revokeObjectURL(capturedUrlRef.current);
    }

    capturedUrlRef.current = url;
    setCapturedUrl(url);
  };

  const closeCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const openCamera = async () => {
    setCameraLoading(true);
    const cameraRequestId = captureRequestRef.current;
    const mediaStream = await navigator.mediaDevices.getUserMedia({
      video: {facingMode: 'user'},
      audio: false
    });

    if (cameraRequestId !== captureRequestRef.current) {
      mediaStream.getTracks().forEach((track) => track.stop());
      return;
    }

    streamRef.current = mediaStream;

    if (liveVideoRef.current) {
      liveVideoRef.current.srcObject = mediaStream;
    }
  };

  const resetState = () => {
    captureRequestRef.current += 1;
    closeCamera();
    setPreviewUrl(null);
    setCameraLoading(true);
    setPhase('camera');
    setImageFile(null);
  };

  useEffect(() => {
    if (!show) {
      return;
    }

    captureRequestRef.current += 1;
    let cancelled = false;

    if (defaultValues.file) {
      setPreviewUrl(URL.createObjectURL(defaultValues.file));
      setImageFile(defaultValues.file);
      setPhase('preview');
      setCameraLoading(false);
    } else {
      setPreviewUrl(null);
      setImageFile(null);
      setPhase('camera');
      void openCamera().catch(() => {
        if (!cancelled) {
          setCameraLoading(false);
          toast('برای برداشت تصویر، دسترسی به دوربین را مجاز کنید');
        }
      });
    }

    return () => {
      cancelled = true;
      captureRequestRef.current += 1;
      closeCamera();
      if (capturedUrlRef.current) {
        URL.revokeObjectURL(capturedUrlRef.current);
        capturedUrlRef.current = null;
      }
    };
  }, [defaultValues.file, show]);

  const handleCapture = () => {
    const video = liveVideoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || video.videoWidth === 0 || video.videoHeight === 0) {
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    context.save();
    context.translate(canvas.width, 0);
    context.scale(-1, 1);
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    context.restore();

    const captureRequestId = ++captureRequestRef.current;

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          return;
        }

        const preview = createImagePreview(
          blob,
          () => captureRequestId === captureRequestRef.current
        );

        if (!preview) {
          return;
        }

        closeCamera();
        setPreviewUrl(preview.previewUrl);
        setImageFile(preview.file);
        setPhase('preview');
      },
      'image/jpeg',
      0.9
    );
  };

  const handleRetake = () => {
    setPreviewUrl(null);
    setImageFile(null);
    setPhase('camera');
    void openCamera().catch(() => {
      setCameraLoading(false);
      toast('برای برداشت تصویر، دسترسی به دوربین را مجاز کنید');
    });
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleUpload = async () => {
    if (!imageFile) {
      return;
    }

    const formData = new FormData();
    formData.append('file', imageFile);
    setUploading(true);

    try {
      const response = await dispatch(uploadFile(formData)).unwrap();

      if (response.isSuccess) {
        onCaptureComplete(response.data.id, imageFile);
        handleClose();
      } else {
        toast(response.message || 'خطا');
      }
    } catch (error) {
      toast(error instanceof Error ? error.message : 'خطا');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      show={show}
      onClose={handleClose}
      canceledOnTouchOutside
      defaultHeader
      title="برداشت تصویر"
    >
      <div className="flex flex-col items-center gap-3 p-4">
        <div className="relative flex size-45 items-center justify-center rounded-full border border-gray-03 bg-gray-03">
          <video
            ref={liveVideoRef}
            autoPlay
            muted
            playsInline
            className={`size-full scale-x-[-1] rounded-full object-cover ${
              phase === 'camera' ? 'block' : 'hidden'
            }`}
            onPlaying={() => setCameraLoading(false)}
          />
          <canvas ref={canvasRef} className="hidden" />
          {phase === 'preview' && capturedUrl ? (
            <img
              src={capturedUrl}
              alt="تصویر برداشت شده"
              className="size-full rounded-full object-cover"
            />
          ) : null}
          {cameraLoading && phase === 'camera' ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loading />
            </div>
          ) : null}
        </div>
        <div className="flex flex-col items-center gap-3">
          {phase === 'camera' ? (
            <>
              <p className="text-body-sm text-gray-09">تصویر کاملی از چهره‌ی خود بارگزاری کنید</p>
              <Button size="md" onClick={handleCapture} disabled={cameraLoading}>
                برداشت تصویر
              </Button>
            </>
          ) : (
            <>
              <Button size="md" variant="fill" color="secondary" onClick={handleRetake}>
                برداشت مجدد
              </Button>
              <Button size="md" onClick={handleUpload} loading={uploading}>
                تایید و ارسال
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ImageCaptureModal;
